import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { useDailyReceiving } from '../hooks/useReports';
import { exportToCSV } from '@/shared/utils/exportUtils';
import { formatDate } from '@/shared/utils/dateUtils';
import type { DailyReceivingFilters } from '../services/reportsService';

interface DailyReceivingSectionProps {
  filters: DailyReceivingFilters;
}

type GroupBy = 'date' | 'manufacturer' | 'location' | 'none';

export function DailyReceivingSection({ filters }: DailyReceivingSectionProps) {
  const { data, isLoading, error } = useDailyReceiving(filters);
  const [groupBy, setGroupBy] = useState<GroupBy>('date');

  const handleExport = () => {
    if (!data) return;

    exportToCSV(
      data.map((item) => ({
        'Received Date': item.received_date,
        Location: item.location_name,
        Manufacturer: item.manufacturer_name,
        Brand: item.brand,
        'Frame Model': item.frame_name_or_model,
        Color: item.color,
        Size: item.eye_size,
        'Order Type': item.order_type,
        'Patient Name': item.patient_name || '',
        'Tray Number': item.tray_number || '',
        'Ordered Date': item.ordered_date || '',
        'Days to Receive': item.days_to_receive || '',
        'PO Number': item.po_number || '',
      })),
      'daily-receiving-report'
    );
  };

  const groupedData = () => {
    if (!data) return {};

    switch (groupBy) {
      case 'date':
        return data.reduce((acc, item) => {
          const key = item.received_date;
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {} as Record<string, typeof data>);
      case 'manufacturer':
        return data.reduce((acc, item) => {
          const key = item.manufacturer_name;
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {} as Record<string, typeof data>);
      case 'location':
        return data.reduce((acc, item) => {
          const key = item.location_name;
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {} as Record<string, typeof data>);
      default:
        return { 'All Frames': data };
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Daily Receiving</h2>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Daily Receiving</h2>
        <div className="text-center py-8 text-red-600">
          Error loading data: {error.message}
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Daily Receiving</h2>
        <div className="text-center py-8 text-gray-500">
          No frames received in the selected date range
        </div>
      </Card>
    );
  }

  const grouped = groupedData();
  const totalFrames = data.length;
  const stockCount = data.filter((item) => item.order_type === 'STOCK').length;
  const patientCount = data.filter((item) => item.order_type === 'PATIENT').length;
  const avgDaysToReceive =
    data
      .filter((item) => item.days_to_receive !== null)
      .reduce((sum, item) => sum + (item.days_to_receive || 0), 0) /
    data.filter((item) => item.days_to_receive !== null).length;

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Daily Receiving</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing data from {formatDate(filters.date_from)} to{' '}
            {formatDate(filters.date_to)}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-2">
          <div className="text-sm text-gray-600">Total Received</div>
          <div className="text-2xl font-bold text-blue-600">{totalFrames}</div>
        </Card>
        <Card className="p-4 border-2">
          <div className="text-sm text-gray-600">Stock Orders</div>
          <div className="text-2xl font-bold text-green-600">{stockCount}</div>
        </Card>
        <Card className="p-4 border-2">
          <div className="text-sm text-gray-600">Patient Orders</div>
          <div className="text-2xl font-bold text-purple-600">{patientCount}</div>
        </Card>
        <Card className="p-4 border-2">
          <div className="text-sm text-gray-600">Avg Days to Receive</div>
          <div className="text-2xl font-bold">
            {isNaN(avgDaysToReceive) ? '-' : avgDaysToReceive.toFixed(1)}
          </div>
        </Card>
      </div>

      {/* Group By Selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Group By
        </label>
        <div className="flex gap-2">
          {(['date', 'manufacturer', 'location', 'none'] as GroupBy[]).map(
            (option) => (
              <Button
                key={option}
                variant={groupBy === option ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setGroupBy(option)}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </Button>
            )
          )}
        </div>
      </div>

      {/* Grouped Tables */}
      <div className="space-y-6">
        {Object.entries(grouped).map(([groupKey, items]) => (
          <div key={groupKey}>
            <h3 className="text-lg font-semibold mb-3 text-gray-800">
              {groupBy === 'date' ? formatDate(groupKey) : groupKey} (
              {items.length} {items.length === 1 ? 'frame' : 'frames'})
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Received Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Manufacturer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Frame Details
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Patient/Tray
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Days to Receive
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      PO#
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {formatDate(item.received_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className="hidden md:inline">{item.location_name}</span>
                        <span className="md:hidden">
                          {item.location_short_code || item.location_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        {item.manufacturer_name}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="max-w-xs">
                          <div className="font-medium">
                            {item.brand} - {item.frame_name_or_model}
                          </div>
                          <div className="text-gray-500">
                            {item.color} | Size {item.eye_size}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge
                          variant={item.order_type === 'PATIENT' ? 'warning' : 'neutral'}
                        >
                          {item.order_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {item.patient_name ? (
                          <div>
                            <div className="font-medium">{item.patient_name}</div>
                            <div className="text-gray-500">Tray {item.tray_number}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {item.days_to_receive !== null ? (
                          <span
                            className={
                              item.days_to_receive <= 7
                                ? 'text-green-600'
                                : item.days_to_receive <= 14
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }
                          >
                            {item.days_to_receive} days
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.po_number || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}


